var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useCallback, useRef, useEffect } from 'react';
import { sendMessage, describeChatSendError, newClientRequestId, isSupportConflict, DUPLICATE_SEND_MESSAGE } from '../services/chatApi';
import { SUPPORT_ERROR } from '../constants/support';
import { TosRequiredError } from '../services/orchestrator-api-client';
import { getMonthlyUsage } from '../services/usageApi';
import { buildUserMessage, buildAgentMessage, DEFAULT_AGENT } from '../helpers';
// How long a deduped send waits for the original turn to replay before the
// widget tells the user nothing new is coming. Generous enough to cover an SSE
// re-attach (token refresh + reconnect + buffer replay).
export const DUPLICATE_REATTACH_GRACE_MS = 5000;
export function useChatInput({ conversationId, appendMessage, pageContext, disabled = false, tosAccepted = true, onTosRequired, trainingMode = false, onDuplicateSend, onSupportSessionActive }) {
    const [inputValue, setInputValue] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState(null);
    // C1: track whether we're waiting for stream_start to clear isSending.
    // Set true after POST /messages resolves; cleared by notifyStreamStarted
    // or the 30s safety timeout.
    const awaitingStreamStartRef = useRef(false);
    const safetyTimerRef = useRef(null);
    // Generation counter — incremented on each new handleSubmit and on resetSending.
    // Lets the POST success callback detect stale sends (e.g. resetSending called
    // while the POST was still in flight) and discard them without updating state.
    const sendGenerationRef = useRef(0);
    // Latch the generation number when stream_start arrives; if it arrives before
    // the POST resolves, the success path clears isSending immediately instead of
    // starting the 30s safety timer.
    const streamStartedGenerationRef = useRef(null);
    // M2: abort controller for the in-flight POST.
    const abortControllerRef = useRef(null);
    const clearSendingState = useCallback(() => {
        setIsSending(false);
        awaitingStreamStartRef.current = false;
        if (safetyTimerRef.current !== null) {
            clearTimeout(safetyTimerRef.current);
            safetyTimerRef.current = null;
        }
    }, []);
    // Cleanup on unmount: abort in-flight request and cancel safety timer
    useEffect(() => {
        return () => {
            var _a;
            (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
            if (safetyTimerRef.current !== null) {
                clearTimeout(safetyTimerRef.current);
            }
        };
    }, []);
    const resetSending = useCallback(() => {
        sendGenerationRef.current += 1; // invalidate any active POST
        streamStartedGenerationRef.current = null;
        clearSendingState();
    }, [clearSendingState]);
    const abortSend = useCallback(() => {
        var _a;
        (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        abortControllerRef.current = null;
    }, []);
    // Called by AIChatWidget when stream_start arrives (responseState → 'receiving').
    // Clears isSending and cancels the safety timer. Also latches the current
    // generation so POST success can detect early-arriving stream_start.
    const notifyStreamStarted = useCallback(() => {
        streamStartedGenerationRef.current = sendGenerationRef.current;
        if (awaitingStreamStartRef.current) {
            clearSendingState();
        }
    }, [clearSendingState]);
    const handleSubmit = useCallback((message_1, fileIds_1, ...args_1) => __awaiter(this, [message_1, fileIds_1, ...args_1], void 0, function* (message, fileIds, skipOptimistic = false) {
        const messageToSend = message !== null && message !== void 0 ? message : inputValue;
        const trimmed = messageToSend.trim();
        if (!trimmed || disabled || isSending || !tosAccepted)
            return;
        // ---------------------------------------------------------------------------
        // /usage slash-command intercept — bypasses orchestrator entirely.
        // Exact match only (case-insensitive). Extra args (e.g. "/usage extra") fall
        // through to normal sendMessage so the orchestrator can respond naturally.
        // ---------------------------------------------------------------------------
        if (trimmed.toLowerCase() === '/usage') {
            setError(null);
            setInputValue('');
            appendMessage(buildUserMessage('/usage'));
            try {
                const usage = yield getMonthlyUsage();
                appendMessage(buildAgentMessage({
                    responseId: `usage-${Date.now()}`,
                    conversationId: conversationId !== null && conversationId !== void 0 ? conversationId : '',
                    agent: DEFAULT_AGENT,
                    blocks: [
                        {
                            type: 'usage_metric',
                            block_id: 'usage-metric-0',
                            block_index: 0,
                            tokens_used: usage.tokensUsed,
                            tokens_limit: usage.tokensLimit,
                            period_label: usage.periodLabel,
                            reset_date: usage.resetDate
                        }
                    ]
                }));
            }
            catch (_err) {
                appendMessage(buildAgentMessage({
                    responseId: `usage-err-${Date.now()}`,
                    conversationId: conversationId !== null && conversationId !== void 0 ? conversationId : '',
                    agent: DEFAULT_AGENT,
                    blocks: [
                        {
                            type: 'markdown',
                            block_id: 'usage-err-0',
                            block_index: 0,
                            content: "Couldn't fetch usage"
                        }
                    ]
                }));
            }
            return;
        }
        setIsSending(true);
        setError(null);
        // Create a fresh AbortController for this send
        abortControllerRef.current = new AbortController();
        // Stamp a generation token before the POST. Checked in the success path to
        // detect stale sends (e.g. resetSending() called while POST was in flight).
        sendGenerationRef.current += 1;
        const generation = sendGenerationRef.current;
        streamStartedGenerationRef.current = null;
        // One idempotency key per send ATTEMPT, minted here where the user acts:
        // a retry of this POST has to reuse it or the graph runs twice, and a
        // deliberate re-ask has to NOT reuse it or the server dedupes a question
        // the user genuinely asked again — the hang this fixes.
        const clientRequestId = newClientRequestId();
        let optimisticMessageId = null;
        try {
            if (!conversationId) {
                throw new Error('Cannot send message without an active conversation id');
            }
            // Optimistic: append user message after conversation guard
            if (!skipOptimistic) {
                const optimistic = buildUserMessage(trimmed);
                optimisticMessageId = optimistic.id;
                appendMessage(optimistic);
            }
            const result = yield sendMessage(conversationId, {
                message: trimmed,
                file_ids: fileIds,
                page_context: pageContext,
                training_mode: trainingMode,
                client_request_id: clientRequestId
            }, abortControllerRef.current.signal);
            // Discard if resetSending() invalidated this send while POST was in flight
            if (sendGenerationRef.current !== generation)
                return;
            setInputValue('');
            // Deduped by the orchestrator (HTTP 200, status 'duplicate'): the graph
            // never ran, so stream_start will NEVER arrive and waiting for it is
            // exactly what left the widget spinning. Leave the in-flight state now —
            // from here the stop button and thinking indicator follow streamState —
            // and ask the host to re-attach in case the original turn is still live.
            if ((result === null || result === void 0 ? void 0 : result.status) === 'duplicate') {
                setIsSending(false);
                awaitingStreamStartRef.current = false;
                if (safetyTimerRef.current !== null)
                    clearTimeout(safetyTimerRef.current);
                onDuplicateSend === null || onDuplicateSend === void 0 ? void 0 : onDuplicateSend(result.response_id);
                safetyTimerRef.current = setTimeout(() => {
                    safetyTimerRef.current = null;
                    // Something replayed (stream_start latched the generation) — the
                    // answer is rendering, so stay quiet.
                    if (sendGenerationRef.current !== generation)
                        return;
                    if (streamStartedGenerationRef.current === generation)
                        return;
                    appendMessage(buildAgentMessage({
                        responseId: `duplicate-send-${Date.now()}`,
                        conversationId,
                        agent: DEFAULT_AGENT,
                        blocks: [
                            {
                                type: 'markdown',
                                block_id: 'duplicate-send-0',
                                block_index: 0,
                                content: DUPLICATE_SEND_MESSAGE
                            }
                        ]
                    }));
                }, DUPLICATE_REATTACH_GRACE_MS);
                return;
            }
            // C1: POST resolved — check whether stream_start already latched early.
            if (streamStartedGenerationRef.current === generation) {
                // stream_start arrived before POST resolved — clear immediately.
                clearSendingState();
            }
            else {
                // Normal path: keep isSending=true until stream_start arrives.
                awaitingStreamStartRef.current = true;
                // Safety fallback: if stream_start never arrives (SSE drop, 5xx), clear
                // isSending after 30s so the UI doesn't hang forever.
                safetyTimerRef.current = setTimeout(() => {
                    if (awaitingStreamStartRef.current && sendGenerationRef.current === generation) {
                        clearSendingState();
                    }
                }, 30000);
            }
        }
        catch (err) {
            // Abort (user-initiated via abortSend/handleStop): don't show an error card.
            const isAborted = (err instanceof Error && (err.name === 'CanceledError' || err.name === 'AbortError')) ||
                (err !== null &&
                    typeof err === 'object' &&
                    'code' in err &&
                    err.code === 'ERR_CANCELED');
            if (!isAborted) {
                if (onSupportSessionActive && isSupportConflict(err, SUPPORT_ERROR.SESSION_ACTIVE)) {
                    onSupportSessionActive(trimmed, optimisticMessageId);
                }
                else if (err instanceof TosRequiredError) {
                    if (onTosRequired) {
                        onTosRequired();
                    }
                    else {
                        setError(new Error('Terms of Service acceptance is required before sending messages.'));
                    }
                }
                else {
                    // Surface the failure to the user as a readable assistant message
                    // instead of silently stopping the spinner. Without this, a failed
                    // POST (network blip, 5xx, or a 422 like message-too-long) leaves
                    // the widget looking hung with no explanation. describeChatSendError
                    // returns the real reason (e.g. the length limit) in plain English —
                    // no technical detail. Mirrors the /usage error path above.
                    setError(err instanceof Error ? err : new Error(String(err)));
                    appendMessage(buildAgentMessage({
                        responseId: `send-err-${Date.now()}`,
                        conversationId: conversationId !== null && conversationId !== void 0 ? conversationId : '',
                        agent: DEFAULT_AGENT,
                        blocks: [
                            {
                                type: 'markdown',
                                block_id: 'send-err-0',
                                block_index: 0,
                                content: describeChatSendError(err)
                            }
                        ]
                    }));
                }
            }
            // Always clear sending state on any error or abort
            clearSendingState();
        }
    }), [
        inputValue,
        disabled,
        isSending,
        tosAccepted,
        conversationId,
        appendMessage,
        pageContext,
        onTosRequired,
        trainingMode,
        onDuplicateSend,
        onSupportSessionActive,
        clearSendingState
    ]);
    return {
        inputValue,
        setInputValue,
        handleSubmit,
        isSending,
        resetSending,
        error,
        abortSend,
        notifyStreamStarted
    };
}
